import { useMemo } from "react";
import type { LocalizedText } from "../i18n/i18n";
import { tenantCapabilityCatalogDefault, tenantCapabilityWithFallback } from "./tenantCapabilitiesRead";
import { useAuth } from "./useAuth";

export type FeatureMinPlan = "studio" | "premium" | "enterprise" | "addon";

export type FeatureAccessMetadata = {
  key: string;
  title: LocalizedText;
  description: LocalizedText;
  badgeText: LocalizedText;
  minPlan: FeatureMinPlan;
  planName?: LocalizedText;
  planPriceText?: LocalizedText;
  addonId?: string;
  addonName?: LocalizedText;
  addonPriceText?: LocalizedText;
  highlights?: LocalizedText[];
};

export const FEATURE_ACCESS_CATALOG: Record<string, FeatureAccessMetadata> = {
  leadManager: {
    key: "leadManager",
    title: { de: "Interessenten-Management", en: "Lead Management" },
    description: {
      de: "Gewinnen Sie mehr Mitglieder mit strukturierten Akquise-Pipelines, Lead-Tracking und automatischer Interessentenverwaltung.",
      en: "Acquire more members with structured sales pipelines, lead tracking, and automated lead management."
    },
    badgeText: { de: "in Premium enthalten", en: "included in Premium" },
    minPlan: "premium",
    planName: { de: "Premium", en: "Premium" },
    planPriceText: { de: "ab 249,00 € netto / Monat", en: "from €249.00 net / month" },
    addonId: "interessenten",
    addonName: { de: "Vertriebs-Pipeline & Lead-Management", en: "Sales Pipeline & Lead Management" },
    addonPriceText: { de: "49,00 € netto / Monat", en: "€49.00 net / month" },
    highlights: [
      { de: "visuelle Vertriebs- und Akquise-Pipelines", en: "visual sales & acquisition pipelines" },
      { de: "automatische Lead-Erfassung und Wiedervorlagen", en: "automated lead intake and follow-ups" },
      { de: "Conversion- und Kampagnenauswertung", en: "conversion and campaign analytics" }
    ]
  },
  memberCare: {
    key: "memberCare",
    title: { de: "Mitgliederbetreuung", en: "Member Care" },
    description: {
      de: "Sichern Sie langfristige Mitgliedschaften durch strukturierte Betreuungspipelines, Check-in-Workflows und proaktive Begleitung.",
      en: "Retain members through structured care pipelines, check-in workflows, and proactive engagement."
    },
    badgeText: { de: "ab Studio", en: "from Studio" },
    minPlan: "studio",
    planName: { de: "Studio", en: "Studio" },
    planPriceText: { de: "ab 149,00 € netto / Monat", en: "from €149.00 net / month" },
    highlights: [
      { de: "Betreuungspipelines (Onboarding, Reaktivierung, Kündigungsabwehr)", en: "care pipelines (onboarding, reactivation, churn prevention)" },
      { de: "ereignisgesteuerte Betreuungsaufgaben am Check-in", en: "event-driven care tasks at check-in" },
      { de: "lückenlose Betreuungshistorie in der Personenakte", en: "complete care history in member records" }
    ]
  },
  staffScheduling: {
    key: "staffScheduling",
    title: { de: "Mitarbeiter & Dienstplan", en: "Staff & Scheduling" },
    description: {
      de: "Erstellen Sie Dienstpläne, erfassen Sie Arbeitszeiten, verwalten Sie Urlaubsanträge und übergeben Sie Stunden an die Lohnabrechnung.",
      en: "Create shift schedules, track work hours, manage leave requests, and export hours to payroll."
    },
    badgeText: { de: "in Premium enthalten", en: "included in Premium" },
    minPlan: "premium",
    planName: { de: "Premium", en: "Premium" },
    planPriceText: { de: "ab 249,00 € netto / Monat", en: "from €249.00 net / month" },
    addonId: "personal",
    addonName: { de: "Personal & Dienstplanung (HR)", en: "Staff & Shift Scheduling (HR)" },
    addonPriceText: { de: "29,00 € netto / Monat", en: "€29.00 net / month" },
    highlights: [
      { de: "visuelle Wochen- und Schichtplanung", en: "visual weekly and shift planning" },
      { de: "Zeiterfassung, Pausenregeln und Überstundenkonten", en: "time tracking, break rules, and overtime accounts" },
      { de: "Urlaubsverwaltung und DATEV-kompatible Lohnübergabe", en: "leave management and payroll export" }
    ]
  },
  articleSales: {
    key: "articleSales",
    title: { de: "Artikelverkauf & Kasse", en: "Point of Sale" },
    description: {
      de: "Verkaufen Sie Getränke, Shakes und Zubehör direkt am Tresen mit Kassenbon-Druck, Barzahlung und Lastschrift-Abbuchung.",
      en: "Sell beverages, shakes, and merchandise directly at reception with receipts, cash, and direct debit."
    },
    badgeText: { de: "ab Studio", en: "from Studio" },
    minPlan: "studio",
    planName: { de: "Studio", en: "Studio" },
    planPriceText: { de: "ab 149,00 € netto / Monat", en: "from €149.00 net / month" },
    addonId: "kasse",
    addonName: { de: "Theken- & Artikelverkauf", en: "Counter & POS Sales" },
    addonPriceText: { de: "19,00 € netto / Monat", en: "€19.00 net / month" },
    highlights: [
      { de: "Schnellkasse für Tresen und Rezeption", en: "quick POS for reception and counter sales" },
      { de: "Barverkauf, EC/Kreditkarte oder Lastschrift über das Mitgliedskonto", en: "cash, card, or direct debit on member account" },
      { de: "Warengruppen- und Kassenberichte", en: "merchandise groups and cash reports" }
    ]
  },
  memberPromotions: {
    key: "memberPromotions",
    title: { de: "Kampagnen & Aktionen", en: "Campaigns & Promotions" },
    description: {
      de: "Steigern Sie Vertragsabschlüsse mit gezielten Promotions, Gutscheinen, Werbepartnern und Startguthaben.",
      en: "Boost contract signups with targeted promotions, vouchers, promotional partners, and credits."
    },
    badgeText: { de: "in Premium enthalten", en: "included in Premium" },
    minPlan: "premium",
    planName: { de: "Premium", en: "Premium" },
    planPriceText: { de: "ab 249,00 € netto / Monat", en: "from €249.00 net / month" },
    addonId: "automation",
    addonName: { de: "Workflow-Automatisierung & Boni", en: "Workflow Automation & Bonuses" },
    addonPriceText: { de: "29,00 € netto / Monat", en: "€29.00 net / month" },
    highlights: [
      { de: "zeitlich befristete Rabatte und Freimonate", en: "limited-time discounts and free months" },
      { de: "Gutschein-Codes und Werbepartner-Provisionen", en: "voucher codes and partner commissions" },
      { de: "automatische Anwendung bei Online- und Vor-Ort-Abschluss", en: "automatic application in online and on-site signups" }
    ]
  },
  automation: {
    key: "automation",
    title: { de: "Automationen & Workflows", en: "Automations & Workflows" },
    description: {
      de: "Automatisieren Sie wiederkehrende Routineaufgaben: Geburtstagsgrüße, Ruhezeit-Bestätigungen und Benachrichtigungen.",
      en: "Automate repetitive routine tasks: birthday greetings, pause confirmations, and notifications."
    },
    badgeText: { de: "in Premium enthalten", en: "included in Premium" },
    minPlan: "premium",
    planName: { de: "Premium", en: "Premium" },
    planPriceText: { de: "ab 249,00 € netto / Monat", en: "from €249.00 net / month" },
    addonId: "automation",
    addonName: { de: "Workflow-Automatisierung & Boni", en: "Workflow Automation & Bonuses" },
    addonPriceText: { de: "29,00 € netto / Monat", en: "€29.00 net / month" },
    highlights: [
      { de: "regelbasierte Auslöser (Zeit, Check-in, Vertragsstatus)", en: "rule-based triggers (time, check-in, contract status)" },
      { de: "automatischer Versand via E-Mail, SMS oder WhatsApp", en: "automated delivery via email, SMS, or WhatsApp" },
      { de: "vollständiges Ausführungsprotokoll", en: "complete execution logs" }
    ]
  },
  debtCollection: {
    key: "debtCollection",
    title: { de: "Inkasso-Übergabe", en: "Debt Collection" },
    description: {
      de: "Übergeben Sie unbezahlte Forderungen nach erfolglosem Mahnlauf vollautomatisch an Inkassopartner.",
      en: "Transfer unpaid claims automatically to debt collection agencies after reminders fail."
    },
    badgeText: { de: "ab Studio", en: "from Studio" },
    minPlan: "studio",
    planName: { de: "Studio", en: "Studio" },
    planPriceText: { de: "ab 149,00 € netto / Monat", en: "from €149.00 net / month" },
    highlights: [
      { de: "automatisierte Übergabe offener Posten", en: "automated handover of open items" },
      { de: "Aktenführung und Rückläufer-Synchronisierung", en: "case management and return syncing" },
      { de: "rechtssichere Dokumentation", en: "legally compliant documentation" }
    ]
  },
  "integration.meta_whatsapp": {
    key: "integration.meta_whatsapp",
    title: { de: "WhatsApp Business API", en: "WhatsApp Business API" },
    description: {
      de: "Erreichen Sie Mitglieder und Interessenten direkt auf dem Smartphone über die offizielle Meta Cloud API.",
      en: "Reach members and prospects directly on their phone using the official Meta Cloud API."
    },
    badgeText: { de: "ab Studio", en: "from Studio" },
    minPlan: "studio",
    planName: { de: "Studio", en: "Studio" },
    planPriceText: { de: "ab 149,00 € netto / Monat", en: "from €149.00 net / month" },
    addonId: "interfaces",
    addonName: { de: "Multi-Channel-Kommunikation", en: "Multi-Channel Communication" },
    addonPriceText: { de: "19,00 € netto / Monat", en: "€19.00 net / month" },
    highlights: [
      { de: "höchste Öffnungsraten für Terminerinnerungen", en: "highest open rates for appointment reminders" },
      { de: "offizielle Meta-zertifizierte Cloud-Schnittstelle", en: "official Meta-certified Cloud interface" },
      { de: "Vorlagenverwaltung mit dynamischen Variablen", en: "template management with dynamic variables" }
    ]
  },
  "integration.onlinebrief24": {
    key: "integration.onlinebrief24",
    title: { de: "Postversand & Hybridbrief", en: "Letter Mail & Hybrid Post" },
    description: {
      de: "Versenden Sie Mahnungen und offizielle Schreiben per Post ohne Drucker, Kuverts und Briefmarken.",
      en: "Send reminders and official letters by postal mail without printers, envelopes, or stamps."
    },
    badgeText: { de: "ab Studio", en: "from Studio" },
    minPlan: "studio",
    planName: { de: "Studio", en: "Studio" },
    planPriceText: { de: "ab 149,00 € netto / Monat", en: "from €149.00 net / month" },
    addonId: "interfaces",
    addonName: { de: "Multi-Channel-Kommunikation", en: "Multi-Channel Communication" },
    addonPriceText: { de: "19,00 € netto / Monat", en: "€19.00 net / month" },
    highlights: [
      { de: "automatischer Druck, Kuvertierung und Frankierung", en: "automated printing, insertion, and franking" },
      { de: "rechtssicherer Mahn- und Kündigungsversand", en: "legally compliant reminder and cancellation mail" },
      { de: "günstiger als eigener Druck und Gang zur Post", en: "cheaper than manual printing and postal trips" }
    ]
  },
  "integration.gateway": {
    key: "integration.gateway",
    title: { de: "Hardware- & Zutritts-Gateway", en: "Hardware & Access Gateway" },
    description: {
      de: "Verbinden Sie Drehkreuze, Transponderleser, Solarien und Schließfächer direkt mit m1member.",
      en: "Connect turnstiles, RFID readers, solariums, and lockers directly to m1member."
    },
    badgeText: { de: "ab Studio", en: "from Studio" },
    minPlan: "studio",
    planName: { de: "Studio", en: "Studio" },
    planPriceText: { de: "ab 149,00 € netto / Monat", en: "from €149.00 net / month" },
    highlights: [
      { de: "Echtzeit-Zutrittskontrolle am Drehkreuz", en: "real-time access control at turnstiles" },
      { de: "offline-fähiger Kontrollabgleich", en: "offline-capable access reconciliation" },
      { de: "sichere WebSocket-Verschlüsselung", en: "secure encrypted WebSocket communication" }
    ]
  }
};

export type FeatureAccessResult = {
  key: string;
  hasAccess: boolean;
  isLocked: boolean;
  metadata: FeatureAccessMetadata;
  showInMenu: boolean;
  upgradeUrl: string;
};

export function getFeatureAccess(
  capabilities: unknown,
  key: string,
  options?: { isOwnerOrAdmin?: boolean }
): FeatureAccessResult {
  const fallback = tenantCapabilityCatalogDefault(key);
  const hasAccess = tenantCapabilityWithFallback(capabilities, key, fallback);
  const isLocked = !hasAccess;

  const metadata: FeatureAccessMetadata = FEATURE_ACCESS_CATALOG[key] ?? {
    key,
    title: { de: key, en: key },
    description: { de: "Funktion in Ihrem aktuellen Tarif nicht enthalten.", en: "Feature not included in your current plan." },
    badgeText: { de: "Upgrade", en: "Upgrade" },
    minPlan: "studio"
  };

  const isOwnerOrAdmin = options?.isOwnerOrAdmin ?? false;
  // In der Navigation: gebuchte Module immer anzeigen.
  // Ungestufte Module werden für normale Mitarbeiter ausgeblendet, für Admins/Inhaber dezent angezeigt.
  const showInMenu = hasAccess || isOwnerOrAdmin;
  const upgradeUrl = metadata.addonId
    ? `/billing?addon=${encodeURIComponent(metadata.addonId)}`
    : `/billing?plan=${encodeURIComponent(metadata.minPlan)}`;

  return {
    key,
    hasAccess,
    isLocked,
    metadata,
    showInMenu,
    upgradeUrl
  };
}

export function useFeatureAccess(key: string): FeatureAccessResult {
  const auth = useAuth();
  const rawCapabilities = auth.info?.capabilities;
  const isOwnerOrAdmin = auth.hasRole("admin") || auth.hasPermission("billing.write");

  return useMemo(() => {
    return getFeatureAccess(rawCapabilities, key, { isOwnerOrAdmin });
  }, [rawCapabilities, key, isOwnerOrAdmin]);
}
